import type { VariantProps } from "class-variance-authority";

import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import * as React from "react";

import { cn } from "../utils/cn";

const typographyVariants = cva("", {
  variants: {
    variant: {
      h1: "scroll-m-20 text-4xl font-extrabold text-foreground-header lg:text-5xl",
      h2: "scroll-m-20 border-b pb-2 text-3xl font-semibold text-foreground-header first:mt-0",
      h3: "scroll-m-20 text-2xl font-semibold text-foreground-header",
      h4: "scroll-m-20 text-xl font-semibold text-foreground-header",
      h5: "scroll-m-20 text-lg font-semibold text-foreground-header",
      h6: "scroll-m-20 text-base font-semibold text-foreground-header",
      p: "leading-7",
      blockquote: "mt-6 border-l-2 pl-6 italic",
      ul: "my-6 ml-6 list-disc [&>li]:mt-2",
      inlineCode:
        "relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold",
      lead: "text-xl text-muted-foreground",
      largeText: "text-lg font-semibold",
      extraLargeText: "text-xl font-semibold",
      text: "text-base font-normal",
      smallText: "text-sm font-normal",
      extraSmallText: "text-xs leading-4 font-normal",
      mutedText: "text-base text-muted-foreground",
      link: "text-primary underline underline-offset-4 hover:text-primary/80",
    },
    weight: {
      medium: "font-medium",
      semibold: "font-semibold",
      bold: "font-bold",
    },
  },
  defaultVariants: {
    variant: "text",
  },
});

type VariantPropType = VariantProps<typeof typographyVariants>;

const variantElementMap: Record<NonNullable<VariantPropType["variant"]>, string> = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  h5: "h5",
  h6: "h6",
  p: "p",
  blockquote: "blockquote",
  inlineCode: "code",
  largeText: "div",
  extraLargeText: "div",
  text: "span",
  smallText: "span",
  extraSmallText: "span",
  lead: "p",
  mutedText: "p",
  ul: "ul",
  link: "a",
};

export interface TypographyProps
  extends React.HTMLAttributes<HTMLElement>, VariantProps<typeof typographyVariants> {
  asChild?: boolean;
  as?: string;
}

const Typography = React.forwardRef<HTMLElement, TypographyProps>(
  ({ className, variant, weight, as, asChild, ...props }, ref) => {
    const Comp = asChild
      ? Slot
      : (as ?? (variant ? variantElementMap[variant] : undefined) ?? "div");
    return (
      <Comp
        className={cn(typographyVariants({ variant, weight, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);

Typography.displayName = "Typography";

export { Typography, typographyVariants };
